import * as React from 'react';
import Icon from '../../../widgets/Icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { useBooleanState } from '../../../hooks';
import { apiRequest } from '../../../APIClient';
import { Button, ColumnProps, Justify, Table } from '@rtkwlf/ui';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../reducers/session';
import { memoizedHardwareSelectors } from '../../../selectors';
import { ConditionalTooltip } from '../../../widgets/LightTooltip';
import { OnboardingModal } from '../OnboardingModal';
import onboardingUtils from '../../../reducers/onboarding/utils';
import { OBHardwareType } from '../../../types';
import { HardwareAllotmentInfo } from './OnboardingHardwareAllotmentInfo';
import { useSortTableName } from '../hooks/useSortTableName';
import { useHistory } from 'react-router-dom';
import { ROUTE_PATH } from '../../../constants';
import { useEffect, useRef } from 'react';
import { ScrollSectionKeys } from './OnboardingSiteConfig';

export const ScannerCompleteButton = () => {
  const dispatch = useDispatch();
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const completed = useSelector(onboardingSelector.getScannersCompleted);

  const onCompleteSection = React.useCallback(() => {
    dispatch(ONBOARDING_ACTIONS.TOGGLE_COMPLETE('scannersSection'));
    dispatch(apiRequest.saveOBScanners());
  }, [dispatch]);

  return (
    <div className="ob-mark-complete-button">
      <Button
        type="primary"
        ghost={completed}
        onClick={onCompleteSection}
        disabled={!canEdit}
        data-cy="scanner-complete-button"
      >
        {completed ? 'Mark Incomplete' : 'Mark Complete'}
      </Button>
    </div>
  );
};

/**
 * a hook to manage the states of the deletion modal
 */
const useScannerDeleteModal = (currentId: string, scannerName: string) => {
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen, openModal, closeModal] =
    useBooleanState(false);

  const onOk = React.useCallback(() => {
    setModalOpen(false);
    dispatch(ONBOARDING_ACTIONS.DELETE_SCANNER(currentId));
    dispatch(apiRequest.saveOBScanners());
  }, [setModalOpen, dispatch, currentId]);

  const modal = (
    <OnboardingModal
      data-cy="scanner-delete-modal"
      title="Delete Scanner"
      visible={modalOpen}
      onCancel={closeModal}
      onOk={onOk}
      okTitle="Yes, delete scanner"
    >
      <p>
        Are you sure you want to delete <b>{scannerName}</b>?
      </p>
    </OnboardingModal>
  );

  return {
    modal,
    openModal,
  };
};
type TableData = {
  key: string;
  name: string;
  site: string;
  model: string;
  ipAddress: string;
  subnetMask: string;
  primaryDns: string;
  finalized: boolean | undefined;
  vanilla: string;
};

export const OnboardingScannerList = () => {
  const dispatch = useDispatch();

  const scannerSectionRef = useRef<null | HTMLDivElement>(null);
  const scanners = useSelector(onboardingSelector.getScanners);
  const sites = useSelector(onboardingSelector.getSites);
  const availableHardware = useSelector(
    memoizedOnboardingSelectors.getAvailableHardware,
  );
  const vanillaScannerEnabled = useSelector(
    featureFlagSelector.onboarding.vanillaScanner,
  );

  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const isAwnUser = useSelector(sessionSelector.isAwnUser);

  const history = useHistory();

  useEffect(() => {
    const { location } = history;
    if (location.search === `view=${ScrollSectionKeys.SCANNERS}`) {
      setTimeout(() => {
        scannerSectionRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      }, 100);
    }
  }, [scannerSectionRef, history]);

  const onAdd = React.useCallback(
    () => history.push(ROUTE_PATH.ONBOARDING.SCANNERS.NEW),
    [history],
  );

  const onEdit = React.useCallback(
    (uuid) => history.push(`${ROUTE_PATH.ONBOARDING.SCANNERS.EDIT}/${uuid}`),
    [history],
  );

  const incompleteScanners = useSelector(
    // getIncompleteScanners is a HOF that returns a selector
    memoizedHardwareSelectors.getIncompleteHardware(OBHardwareType.SCANNER),
  );

  const canAdd = isAwnUser || Object.keys(incompleteScanners).length > 0;

  React.useEffect(() => {
    if (Object.keys(scanners).length === 0) {
      dispatch(apiRequest.getOBScanners());
    }
    // only fetch once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [currentId, setCurrentId] = React.useState('');

  const { modal: deleteModal, openModal: onDelete } = useScannerDeleteModal(
    currentId,
    scanners[currentId]?.name ?? '',
  );

  const editButtonOnClick = React.useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const id = e.currentTarget.dataset.id;
      if (id) {
        onEdit(id);
      }
    },
    [onEdit],
  );
  const deleteButtonOnClick = React.useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const id = e.currentTarget.dataset.id;
      if (id) {
        setCurrentId(id);
        onDelete();
      }
    },
    [setCurrentId, onDelete],
  );
  const { sort, handleSort } = useSortTableName<TableData>();
  const formattedScanners: TableData[] = React.useMemo(() => {
    return sort(
      Object.keys(scanners).map((id) => {
        const scanner = scanners[id];

        const vanilla = scanner.vanilla ? 'Yes' : 'No';

        return {
          key: id,
          name: scanner.name,
          site: sites[scanner.siteId]?.name ?? '',
          model:
            onboardingUtils.getHardwareInfoByHardwareKey(
              availableHardware.scanners,
              scanner.model,
            )?.label ?? scanner.model,
          ipAddress: scanner.ipAddress,
          subnetMask: scanner.subnetMask,
          primaryDns: scanner.primaryDns,
          finalized: !!sites[scanner.siteId]?.finalized,
          vanilla,
        };
      }),
    );
  }, [scanners, sites, availableHardware.scanners, sort]);

  const columns: ColumnProps<TableData>[] = React.useMemo(() => {
    const result: ColumnProps<TableData>[] = [
      {
        key: 'name',
        dataIndex: 'name',
        title: 'Name',
        sortable: true,
      },
      {
        key: 'site',
        dataIndex: 'site',
        title: 'Site',
      },
      {
        key: 'model',
        dataIndex: 'model',
        title: 'Model',
      },
      {
        key: 'ipAddress',
        dataIndex: 'ipAddress',
        title: 'IP Address',
      },
      {
        key: 'subnetMask',
        dataIndex: 'subnetMask',
        title: 'Subnet Mask',
      },
      {
        key: 'primaryDns',
        dataIndex: 'primaryDns',
        title: 'Primary DNS Server',
      },
    ];

    if (vanillaScannerEnabled) {
      result.splice(3, 0, {
        key: 'vanilla',
        dataIndex: 'vanilla',
        title: 'Customer Configured',
      });
    }

    if (canEdit) {
      result.push({
        key: 'actions',
        title: 'Actions',
        width: 5,
        render: ({ record: { finalized, key } }) => {
          if (!finalized) {
            return (
              <>
                <Icon.Pencil
                  data-cy={`edit-scanner-${key}`}
                  data-id={key}
                  className="mr-2"
                  style={{ cursor: 'pointer' }}
                  onClick={editButtonOnClick}
                />
                <Icon.X
                  data-cy={`delete-scanner-${key}`}
                  data-id={key}
                  style={{ cursor: 'pointer' }}
                  onClick={deleteButtonOnClick}
                />
              </>
            );
          } else {
            return <></>;
          }
        },
        justify: 'flex-end' as Justify,
      });
    }
    return result;
  }, [canEdit, editButtonOnClick, deleteButtonOnClick]);

  return (
    <>
      <HardwareAllotmentInfo type={OBHardwareType.SCANNER} />

      {canEdit && (
        <div
          className="d-flex flex-row-reverse"
          data-cy="add-scanner-div"
          ref={scannerSectionRef}
        >
          <ConditionalTooltip
            visible={!canAdd}
            tooltip="Contact your Onboarding team to purchase additional scanners."
          >
            <Button
              type="primary"
              ghost
              className="mb-2"
              onClick={onAdd}
              disabled={!canAdd}
              data-cy="add-scanner-button"
            >
              <Icon.Plus className="mr-2" />
              Configure Scanner
            </Button>
          </ConditionalTooltip>
        </div>
      )}

      <Table
        data={formattedScanners}
        dataUniqueKey="value"
        columns={columns}
        onSort={handleSort}
        emptyComponent={
          canEdit &&
          canAdd && (
            <div
              className="my-5 text-center"
              data-cy="add-scanner-table-placeholder"
            >
              <span>
                Select
                <Button
                  type="link"
                  className="d-inline px-1 font-normal"
                  onClick={onAdd}
                >
                  Configure Scanner
                </Button>
                to configure an included scanner
              </span>
            </div>
          )
        }
      />

      {deleteModal}
    </>
  );
};

export default OnboardingScannerList;
